/**
 * Dropdown Component
 * A reusable dropdown web component that handles outside clicks, toggling, and accessibility
 */

class DropdownComponent extends HTMLElement {
  constructor() {
    super();
    
    // Initialize state
    this.isOpen = false;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    this.isInsideDrawer = false; // Track if inside a drawer
    this.skipNextToggleKeyUp = false; // Prevent immediate reopen after selection
    
    // Set up mutation observer to handle DOM updates
    this.observer = new MutationObserver(() => {
      this.updateComponentElements();
    });
    
    // Bind methods to preserve context
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    this.updateFocusableElements = this.updateFocusableElements.bind(this);
  }
  
  connectedCallback() {
    // Observe changes to this component's DOM
    this.observer.observe(this, { childList: true, subtree: true });
    
    // Check if this dropdown is inside a slide-drawer
    this.isInsideDrawer = this.isInsideSlideDrawer();
    
    // Initial setup of elements
    this.updateComponentElements();
    
    // Add global click handler
    document.addEventListener('click', this.handleOutsideClick);
    
    // Add key event handlers for accessibility
    this.addEventListener('keyup', this.handleKeyUp);
    this.addEventListener('keydown', this.handleKeyDown);
    
    // If inside a drawer, add special handling
    if (this.isInsideDrawer) {
      console.log('Dropdown component detected inside slide-drawer');
    }
  }
  
  isInsideSlideDrawer() {
    // Walk up the DOM tree to check if we're inside a slide-drawer
    let parent = this.parentNode;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === 'slide-drawer') {
        return true;
      }
      if (parent.host && parent.host.tagName && parent.host.tagName.toLowerCase() === 'slide-drawer') {
        // Check for shadow DOM case
        return true;
      }
      parent = parent.parentNode || (parent.host ? parent.host : null);
    }
    return false;
  }
  
  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('click', this.handleOutsideClick);
    this.removeEventListener('keyup', this.handleKeyUp);
    this.removeEventListener('keydown', this.handleKeyDown);
    
    // Stop observing
    this.observer.disconnect();
    
    // Clean up toggle button event listener
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.toggleDropdown);
    }
  }
  
  updateComponentElements() {
    // Find toggle button and dropdown content by data attributes
    this.toggleButton = this.querySelector('[data-dropdown-toggle]');
    this.dropdownContent = this.querySelector('[data-dropdown-content]');
    this.dropdownArrow = this.querySelector('[data-dropdown-arrow]');
    
    // If elements found, set up event listeners
    if (this.toggleButton && this.dropdownContent) {
      this.toggleButton.removeEventListener('click', this.toggleDropdown);
      this.toggleButton.addEventListener('click', this.toggleDropdown);
      
      // Ensure dropdown has proper ARIA attributes for accessibility
      this.toggleButton.setAttribute('aria-haspopup', 'true');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.dropdownContent.setAttribute('role', 'menu');
      
      // Add additional accessibility attributes for keyboard navigation
      this.dropdownContent.setAttribute('aria-orientation', 'vertical');
      this.toggleButton.setAttribute('aria-controls', 'dropdown-menu');
      this.dropdownContent.id = 'dropdown-menu';
      
      // Set initial state
      this.dropdownContent.classList.add('hidden');
      this.isOpen = false;
      
      // Update focusable elements for keyboard navigation
      this.updateFocusableElements();
    }
  }
  
  updateFocusableElements() {
    // Find all focusable elements inside dropdown content
    const selector = 'a[href], button, input:not(.sr-only), textarea, select, details, [tabindex]:not([tabindex="-1"]), .dropdown-item';
    this.focusableElements = Array.from(this.dropdownContent.querySelectorAll(selector))
      .filter(el => !el.hasAttribute('disabled') && el.getAttribute('tabindex') !== '-1');
    
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    
    // Set tabindex to ensure the dropdown content can receive focus
    this.dropdownContent.setAttribute('tabindex', '-1');
    
    // Make sure interactive items have correct roles
    this.focusableElements.forEach(el => {
      if (el.tagName === 'A' || el.tagName === 'BUTTON') {
        // Only set role if not already set
        if (!el.hasAttribute('role')) {
          el.setAttribute('role', 'menuitem');
        }
        
        el.setAttribute('tabindex', '0');
      } else if (el.classList.contains('dropdown-item')) {
        // Ensure dropdown items are properly focusable
        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '0');
        }
      }
    });
  }
  
  toggleDropdown(event) {
    if (event) {
      event.stopPropagation();
    }
    
    if (!this.dropdownContent) return;
    
    // Opening/closing via explicit toggle should clear skip flag
    this.skipNextToggleKeyUp = false;
    
    this.isOpen = !this.isOpen;
    
    if (this.isOpen) {
      this.dropdownContent.classList.remove('hidden');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      if (this.dropdownArrow) {
        this.dropdownArrow.style.transform = 'rotate(180deg)';
      }
      
      // Update focusable elements
      this.updateFocusableElements();
      
      // Only focus on options if opened with keyboard (not mouse click)
      const isKeyboardEvent = event && (event.type === 'keydown' || event.type === 'keyup');
      
      // Set a small timeout to ensure the dropdown is visible before focusing
      setTimeout(() => {
        if (isKeyboardEvent) {
          // Find selected option if it exists
          const selectedOption = this.findSelectedOption();
          
          if (selectedOption) {
            // Focus on the selected option
            selectedOption.focus();
          } else if (this.firstFocusableElement) {
            // Fall back to first option if no selection
            this.firstFocusableElement.focus();
          }
        } else {
          // Just focus the dropdown itself for mouse interactions
          this.dropdownContent.focus();
        }
      }, 10);
    } else {
      this.dropdownContent.classList.add('hidden');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      if (this.dropdownArrow) {
        this.dropdownArrow.style.transform = 'rotate(0deg)';
      }
      // Return focus to toggle button when closing
      this.toggleButton.focus();
    }
    
    // Dispatch custom event that other components can listen for
    this.dispatchEvent(new CustomEvent('dropdown-toggle', { 
      bubbles: true,
      detail: { isOpen: this.isOpen }
    }));
  }
  
  closeDropdown(event) {
    if (!this.isOpen) return;
    
    // Debug info for troubleshooting
    if (this.isInsideDrawer) {
      console.debug('Closing dropdown inside drawer', { 
        hasEvent: !!event,
        eventType: event?.type,
        eventKey: event?.key,
        eventTarget: event?.target
      });
    }
    
    this.isOpen = false;
    this.dropdownContent.classList.add('hidden');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    
    if (this.dropdownArrow) {
      this.dropdownArrow.style.transform = 'rotate(0deg)';
    }
    
    // Return focus to toggle button when closing
    if (this.toggleButton) {
      this.toggleButton.focus();
    }
    
    this.skipNextToggleKeyUp = false;
    
    // If inside drawer and event is provided, prevent it from closing the drawer
    // This is a safety check in case the event reaches this method directly
    // without going through handleKeyUp first
    if (this.isInsideDrawer && event && event.type === 'keyup' && event.key === 'Escape') {
      console.debug('Preventing Escape propagation in closeDropdown');
      event.stopPropagation();
      event.preventDefault();
    }
    
    this.dispatchEvent(new CustomEvent('dropdown-closed', { 
      bubbles: true,
      detail: { event: event } 
    }));
  }
  
  handleOutsideClick(event) {
    // If dropdown is not open or click was inside this component, do nothing
    if (!this.isOpen || this.contains(event.target)) {
      return;
    }
    
    // Close dropdown when clicking outside
    this.closeDropdown(event);
  }
  
  handleKeyUp(event) {
    // Close dropdown on ESC key press
    if (event.key === 'Escape') {
      // Stop event propagation to prevent closing parent containers (like slide-drawer)
      // This prevents the ESC key from bubbling up to parent elements like slide-drawer
      // which would otherwise also close when the dropdown closes
      event.stopPropagation();
      event.preventDefault();
      
      this.closeDropdown(event);
      
      // Explicitly return to prevent further processing
      return;
    }
    
    // Handle opening dropdown with Space or Enter key
    if ((event.key === 'Enter' || event.key === ' ') && 
        event.target === this.toggleButton &&
        document.activeElement === this.toggleButton && 
        !this.isOpen) {
      if (this.skipNextToggleKeyUp) {
        this.skipNextToggleKeyUp = false;
        return;
      }
      event.preventDefault();
      this.toggleDropdown(event);
      return;
    }
    
    // Handle arrow key navigation within dropdown
    if (!this.isOpen) return;
    
    const currentFocusIndex = this.focusableElements.findIndex(el => el === document.activeElement);
    
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        if (currentFocusIndex < this.focusableElements.length - 1) {
          // Focus next element and ensure it's visible in the dropdown view
          const nextElement = this.focusableElements[currentFocusIndex + 1];
          nextElement.focus();
          nextElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          // Wrap to first element
          this.firstFocusableElement?.focus();
          this.firstFocusableElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        break;
        
      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        if (currentFocusIndex > 0) {
          // Focus previous element and ensure it's visible in the dropdown view
          const prevElement = this.focusableElements[currentFocusIndex - 1];
          prevElement.focus();
          prevElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          // Wrap to last element
          this.lastFocusableElement?.focus();
          this.lastFocusableElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        break;
        
      case 'Home':
        event.preventDefault();
        this.firstFocusableElement?.focus();
        break;
        
      case 'End':
        event.preventDefault();
        this.lastFocusableElement?.focus();
        break;
    }
  }
  
  handleKeyDown(event) {
    // Handle Tab key for focus trapping when open
    if (this.isOpen && event.key === 'Tab') {
      this.trapFocus(event);
    }
    
    // Handle toggle button Space/Enter when dropdown is closed
    if (!this.isOpen && 
        (event.key === 'Enter' || event.key === ' ') && 
        document.activeElement === this.toggleButton) {
      event.preventDefault();
      this.toggleDropdown(event);
      return;
    }
    
    // Handle Space/Enter for selection on focused dropdown items
    if (this.isOpen && 
        (event.key === 'Enter' || event.key === ' ') && 
        document.activeElement !== this.dropdownContent && 
        document.activeElement !== this.toggleButton) {
      event.preventDefault();
      document.activeElement.click();
      
      // Mark to skip the subsequent keyup triggered by this selection
      this.skipNextToggleKeyUp = true;
    }
  }
  
  trapFocus(event) {
    if (!this.isOpen || this.focusableElements.length === 0) return;
    
    // If shift + tab on first element, move to last element
    if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
      event.preventDefault();
      this.lastFocusableElement.focus();
    }
    
    // If tab on last element, move to first element
    else if (!event.shiftKey && document.activeElement === this.lastFocusableElement) {
      event.preventDefault();
      this.firstFocusableElement.focus();
    }
  }
  
  findSelectedOption() {
    // This method finds the currently selected option in the dropdown
    // Check common attributes used to indicate selection
    const selectedOption = this.focusableElements.find(element => {
      // Check for common selection indicators
      return element.getAttribute('aria-selected') === 'true' ||
             element.getAttribute('data-selected') === 'true' ||
             element.classList.contains('selected') ||
             element.classList.contains('active') ||
             element.getAttribute('aria-current') === 'true' ||
             element.hasAttribute('selected');
    });
    
    return selectedOption || null;
  }
}

// Register the component
customElements.define('dropdown-component', DropdownComponent);
